import React from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { PSCredential } from '../../models/PSCredential'
import { RecordFactory } from '../../models/RecordFactory'
import { apiClient } from '../../modules/apiclient'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import Tag from '../app/design/Tag'
import { ListHeader } from '../common/ListHeader'
import { Select } from '../common/fields/Select'

const CalendarStatus: React.FC<{ credential: PSCredential }> = ({ credential }) => {
    if (credential.failingDate !== undefined) {
        return <Tag label="Disconnected" intent="danger" className="w-fit" size="sm" />
    }

    return <Tag label="Connected" intent="success" className="w-fit" size="sm" />
}

const viewWillAppear = async () => {
    await RecordFactory.getInstance().fetchCredentials()

    const [calendars, credentials] = await Promise.all([
        RecordFactory.getInstance().loadCalendars(),
        RecordFactory.getInstance().loadCredentials('google'),
    ])

    return { calendars, credentials }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const { calendars, credentials } = data
    const navigate = usePSNavigate()

    const startGoogleAuth = async (credential?: PSCredential) => {
        try {
            const resp = await apiClient.get(
                '/auth/google' + (!!credential?.id ? `?credential=${credential?.id}` : '')
            )
            window.location.href = resp.data.url
        }
        catch (error) {
            base.logAndDisplayError(error)
        }
    }

    const disconnectAuth = async (credential: PSCredential) => {
        base.showProgressBar('Disconnecting...')

        const credentialCalendars = calendars.filter(
            (calendar) => calendar.credential === credential.id
        )

        credentialCalendars.forEach((calendar) => {
            calendar.syncTo = false
            calendar.syncFrom = false
        })

        await Promise.all([
            RecordFactory.getInstance().sendCalendars(credentialCalendars),
            RecordFactory.getInstance().cancelCredential(credential),
        ])

        base.hideProgressBar()
        base.reload()
    }

    const onEdit = (credential: PSCredential) => {
        if (credential.failingDate !== undefined) {
            startGoogleAuth(credential)
            return
        }

        navigate(`/settings/calendar/setup/${credential.id}`)
    }

    const getCredentialState = (credential: PSCredential) => {
        if (credential.failingDate !== undefined) {
            return 'disconnected'
        }

        if (
            calendars.find(
                (calendar) =>
                    calendar.credential === credential.id && (calendar.syncTo || calendar.syncFrom)
            )
        ) {
            return 'connected'
        }

        return 'unconfigured'
    }

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-6">
            <ListHeader
                title="Calendar sync"
                backButton={{
                    label: 'Settings',
                    href: '/settings',
                }}
                actionButton={{
                    intent: 'primary',
                    label: 'Sync Google Calendar',
                    icon: 'calendarRefresh.small',
                    onClick: () => startGoogleAuth(),
                }}
            />
            <div className="mt-6 rounded-lg bg-white p-8">
                <Table>
                    <TableHeader>
                        <TableRow location="head">
                            <TableHead>Account</TableHead>
                            <TableHead className="font-normal">Status</TableHead>
                            <TableHead className="font-normal"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {credentials.map((credential) => (
                            <TableRow
                                className="cursor-pointer"
                                key={credential.id}
                                onClick={() => onEdit(credential)}
                            >
                                <TableCell>
                                    <div className="flex items-center gap-x-4">
                                        <Icon icon="calendarRefresh.medium" background="round" />
                                        <Paragraph size="sm" className="font-bold">
                                            {credential.email || 'Google Calendar'}
                                        </Paragraph>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <CalendarStatus credential={credential} />
                                </TableCell>
                                <TableCell className="w-10 text-right">
                                    <div className="flex justify-end">
                                        <Select
                                            align="end"
                                            trigger={
                                                <Button
                                                    size="small"
                                                    shape="square"
                                                    intent="tertiary_deprecated"
                                                    onClick={(e: { stopPropagation: () => any }) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Icon
                                                        icon="moreHorizontal.medium"
                                                        className="text-gray-500"
                                                    />
                                                </Button>
                                            }
                                            options={[
                                                {
                                                    id: 'edit',
                                                    icon:
                                                        getCredentialState(credential) ===
                                                        'disconnected'
                                                            ? 'refresh'
                                                            : 'pencil',
                                                    title:
                                                        getCredentialState(credential) ===
                                                        'disconnected'
                                                            ? 'Reconnect'
                                                            : 'Edit',
                                                },
                                                {
                                                    id: 'disconnect',
                                                    icon: 'close',
                                                    title: 'Disconnect',
                                                    isDanger: true,
                                                },
                                            ]}
                                            onSelectOption={(option) => {
                                                if (option.id === 'edit') {
                                                    onEdit(credential)
                                                }
                                                else if (option.id === 'disconnect') {
                                                    disconnectAuth(credential)
                                                }
                                            }}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {credentials.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} className="py-8 text-center">
                                    <Paragraph size="md" className="text-gray-500">
                                        No calendars connected
                                    </Paragraph>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}

export const SetupCalendarSyncPage = usePSComponent(render, viewWillAppear)
